import { portalStoreToken } from '@servicetitan/cp-ui';
import { useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { observer } from 'mobx-react';
import { FC, useState } from 'react';
import { FeatureFlagKeys } from '../../../../feature-flags';
import { PortalAuthStore } from '../../../auth/stores/portal-auth.store';
import { CustomBannerSettingsStore } from '../../../dashboard/api/custom-banner-settings.store';
import { WebPortalStore } from '../../stores/portal.store';
import { bannerDismissKey, dismissBanner, isBannerDismissed } from '../../utils/banner-dismissal';
import { BannerStyleStatus } from '../../utils/banner-settings';
import { BannerMessage } from '../banner-message';
import * as Styles from './portal-banner.module.less';

export interface PortalBannerProps {
    style: BannerStyleStatus;
}

export const PortalBanner: FC<PortalBannerProps> = observer(({ style }) => {
    const { enableCustomBanners } = useFlags<FeatureFlagKeys>();
    const [{ authorized }] = useDependencies(PortalAuthStore);
    const [{ settings, user }] = useDependencies<[WebPortalStore]>(portalStoreToken as any);
    const [bannerStore] = useDependencies(CustomBannerSettingsStore);
    const [closedThisLoad, setClosedThisLoad] = useState(false);

    if (!enableCustomBanners || !authorized || closedThisLoad || !bannerStore.shouldShow) {
        return null;
    }

    const banner = bannerStore.value;
    if (banner?.style !== style) {
        return null;
    }

    const userId = user?.id;
    const dismissKey = bannerDismissKey({
        host: settings?.host,
        scope: 'inportal',
        userId,
        version: banner.version,
    });

    if (isBannerDismissed(dismissKey)) {
        return null;
    }

    const onClose = () => {
        dismissBanner({
            host: settings?.host,
            scope: 'inportal',
            userId,
            version: banner.version,
        });
        setClosedThisLoad(true);
    };

    return (
        <div
            className={classNames(Styles.banner, {
                [Styles.announcement]: style === BannerStyleStatus.Announcement,
                [Styles.alert]: style === BannerStyleStatus.Alert,
            })}
            data-banner-style={style === BannerStyleStatus.Announcement ? 'announcement' : 'alert'}
            data-cy="portal-banner"
        >
            <BannerMessage
                color={banner.color}
                messageHtml={banner.messageHtml}
                onClose={onClose}
                style={banner.style}
            />
        </div>
    );
});
